import qs from 'qs'
import {
  AutoCompleteBrandGroupResult,
  BrandGroup,
  UpdateBrandGroupPayload,
} from 'types/groups'

import {
  BrandGroupDetail,
  CreateMarketingGroupCampaignPayload,
  FbFeedResponse,
  InitFbFeedPayload,
  ListMarketingAdAccountsPayload,
} from '@realty/portal'

import { PaginatedTable } from '../types/common'
import api from '../utils/api'

export const getAllGroups = async ({
  queryKey,
}: any): Promise<PaginatedTable<BrandGroup>> => {
  const result = await api.get(
    `/groups/all?fields=id,displayInfo,brandingId,integrationBehavior,forceRegId,staging,role&${qs.stringify(
      queryKey[1],
    )}`,
  )

  return result.data
}

export const getAllGroupsForAutoComplete = async (
  change: string,
  linkedBranding: undefined | { id: number },
  selectedTenant:
    | undefined
    | { id: string; tenantId: number; createdAt: Date }
    | null,
): Promise<AutoCompleteBrandGroupResult[]> => {
  let url = `/filters/brandinggroups?searchText=${change}`

  if (linkedBranding) {
    url = url + `&linkedBranding=${linkedBranding.id}&brandingGroupRole=office`
  }

  if (selectedTenant?.id) {
    url = url + `&tenant=${selectedTenant.id}`
  }
  const result = await api.get(url)

  return result.data
}
export const getAllGroupsAvailable = (memberId: number) => {
  return api.get(`/groups/available/${memberId}`)
}

export const createGroup = (group: UpdateBrandGroupPayload) => {
  return api.post('/groups/create', group)
}

export const deleteGroup = (group: { groupId: number; brandingId: number }) => {
  return api.post('/groups/delete', {
    brandGroupId: group.groupId,
    brandId: group.brandingId,
  })
}

export const showGroup = async (id: number): Promise<BrandGroupDetail> => {
  const result = await api.get(`/groups/fetch/${id}`)

  return result.data
}


export const getGroupActivity = async (
  id: number,
): Promise<BrandGroupDetail> => {
  const result = await api.get(`/groups/activity/${id}`)

  return result.data
}
export const getLocationsByGroup = (brandId: number, groupId: number) => {
  // return api.get(`/groups/location/fetch/${brandId}/${groupId}`);
  return api.get(`/groups/location/fetch/${brandId}/${groupId}`)
}

export const updateGroup = (group: UpdateBrandGroupPayload) => {
  return api.post('/groups/updatev2', group)
}

export const assignMemberToGroup = (
  memberId: number,
  group: number[],
  brandAdmin = false,
) => {
  return api.post('/members/branding/groups/assign', {
    memberId: memberId,
    brandGroupId: group,
    brandingGroupAdmin: brandAdmin,
  })
}

export const memberToggleGroupAdmin = (
  memberId: number,
  group: number,
  brandAdmin: boolean,
) => {
  return api.post('/members/branding/groups/toggle-admin', {
    memberId: memberId,
    brandGroupId: group,
    brandingGroupAdmin: brandAdmin,
  })
}

export const unassignMemberToGroup = (
  memberId: number,
  group: number | number[],
) => {
  return api.post('/members/branding/groups/unassign', {
    memberId: memberId,
    brandGroupId: group,
  })
}

export const removeGroup = (user_id: number, group_id: number) => {
  return api.post('/super_admin/groups/update', {
    user_id: user_id,
    group_id: group_id,
  })
}

export const getPingStatsByGroup = (group: BrandGroupDetail) => {
  return api.post('/groups/ping-stats', {
    brandGroupId: group.id,
    brandId: group.branding_id,
  })
}

export const getPingZipsByGroup = (group: BrandGroupDetail) => {
  return api.post('/groups/ping-zips', {
    brandGroupId: group.id,
    brandId: group.branding_id,
  })
}

export const resetPingZipsByGroup = (
  group: BrandGroupDetail,
  radius: number,
) => {
  return api.post('/groups/reset-zips', {
    brandGroupId: group.id,
    brandId: group.branding_id,
    radius: radius,
  })
}

export const syncBoberdooFiltersets = (payload: {
  group: number
  partner_id?: number
  parent_partner_id?: number
  partner_type: string
  add_locations?: boolean
}) => {
  return api.post('/boberdoo/sync-filtersets', payload)
}

export const detectParentPartner = async (groupId: number): Promise<{
  parent_partner_id: number | null
  parent_branding_name: string | null
  parent_branding_id: number | null
  already_configured: boolean
}> => {
  const result = await api.get(`/boberdoo/detect-parent-partner?group=${groupId}`)
  return result.data
}

export const getFilterSetsByGroup = async (
  paramsOrContext: any,
): Promise<any> => {
  const params = paramsOrContext?.queryKey
    ? paramsOrContext.queryKey[1]
    : paramsOrContext
  const result = await api.get(
    `/filter-sets?${qs.stringify(params)}`,
  )
  return result.data
}

export const createFilterSet = (payload: any) => {
  return api.post('/filter-sets', payload)
}

export const createFilterSetsFromTemplates = (payload: { branding_group_id: number; template_ids: number[] }) => {
  return api.post('/filter-sets/from-templates', payload)
}

export const getFilterSetTemplates = () => {
  return api.get('/filter-templates')
}

export const updateFilterSet = (id: number, payload: any) => {
  return api.put(`/filter-sets/${id}`, payload)
}

export const deleteFilterSet = (id: number) => {
  return api.delete(`/filter-sets/${id}`)
}

export const bulkActionFilterSets = (ids: number[], action: 'activate' | 'deactivate' | 'delete') => {
  return api.post('/filter-sets/bulk-action', { ids, action })
}

export const getPingFilterableFields = async (): Promise<any> => {
  const result = await api.get('/ping-filterable-fields')
  return result.data
}

export const setupBoberdoo = async (
  groupId: number,
  partnerType: string,
  createTemplates?: boolean,
): Promise<any> => {
  const result = await api.post('/boberdoo-setup', {
    group: groupId,
    partner_type: partnerType,
    create_templates: createTemplates,
  })
  return result.data
}

export const syncBoberdooZips = async (groupId: number): Promise<any> => {
  const result = await api.post('/boberdoo/sync-zips', { group: groupId })
  return result.data
}

export const createBoberdooPartner = async (groupId: number): Promise<any> => {
  const result = await api.post('/boberdoo/create-partner', { group: groupId })
  return result.data
}

export const createBoberdooSubPartner = async (
  groupId: number,
  parentPartnerId?: number,
): Promise<any> => {
  const result = await api.post('/boberdoo/create-sub-partner', {
    group: groupId,
    parent_partner_id: parentPartnerId,
  })
  return result.data
}

export const createBoberdooCatchall = async (groupId: number): Promise<any> => {
  const result = await api.post('/boberdoo/create-catchall', { group: groupId })
  return result.data
}

export const addPingZipsBulk = (group: BrandGroupDetail, zips: number[]) => {
  return api.post('/groups/add-zips', {
    brandGroupId: group.id,
    brandId: group.branding_id,
    zips,
  })
}

export const deletePingZipsByGroup = (group: BrandGroupDetail, zip: number) => {
  return api.post('/groups/delete-zips', {
    brandGroupId: group.id,
    brandId: group.branding_id,
    zip: zip,
  })
}

export const getAdAccounts = async (
  payload: ListMarketingAdAccountsPayload,
) => {
  const result = await api.get(`/groups/campaigns/accounts`, {
    params: {
      ...payload,
    },
  })

  return result.data
}

export const getGeneratedBlogs = async ({ queryKey }) => {
  console.log({ queryKey })
  const result = await api.get(`/generated-contents/${queryKey[1]}`)

  return result.data
}

export const getGeneratedBlogById = async ({ queryKey }) => {
  console.log({ queryKey })
  const result = await api.get(
    `/generated-contents/${queryKey[1]}/${queryKey[2]}`,
  )

  return result.data
}

export const updateGeneratedContent = async (payload, brandingGroupId, id) => {
  const result = await api.post(
    `/generated-contents/${brandingGroupId}/${id}`,
    payload,
  )

  return result.data
}

export const getCampaignsByGroup = async ({ queryKey }) => {
  const result = await api.get(`/groups/campaigns?${qs.stringify(queryKey[1])}`)

  return result.data
}

//   export const getCampaignsStatsByGroup = (group: BrandGroup) => {
//     return api.get(`/groups/campaigns/stats`, {
//         params: {
//             owner_id: group.id,
//         }
//     })
//   }
;``

export const getCampaignsStatsByGroup = async ({ queryKey }: any) => {
  const result = await api.get(
    `/groups/campaigns/stats?${qs.stringify(queryKey[1])}`,
  )
  return result.data
}

export const createCampaign = (
  campaign: CreateMarketingGroupCampaignPayload,
) => {
  return api.post('/groups/campaigns', campaign)
}

// Fetch the FB ad-feed status for a group. queryKey[1] is the branding group id.
// Returns the FbFeedResponse envelope; data is null when no feed exists.
export const getFbFeed = async ({ queryKey }: any): Promise<FbFeedResponse> => {
  const result = await api.get(`/groups/${queryKey[1]}/fb-feed`)
  return result.data
}

// Initialize a new FB ad-feed. created_by is omitted — the server injects it
// from the authed member; the client only sends owner_id + filters + a location
// selection.
export const initFbFeed = (feed: Omit<InitFbFeedPayload, 'created_by'>) => {
  return api.post('/groups/fb-feed/init', feed)
}

export const getReviewsByGroup = async (brandId: number, groupId: number) => {
  const result = await api.get(`/groups/reviews/${brandId}/${groupId}`)
  return result.data
}

export const createReview = async (payload: any) => {
  const result = await api.post('/groups/reviews/create', payload)
  return result.data
}

export const createBulkReviews = async (payload: {
  brandId: number
  groupId: number
  reviews: Array<{
    review: string
    rating: number
    author: string
    title: string
    source: string
    external_id?: number | string
  }>
}) => {
  const result = await api.post('/groups/reviews/bulk-create', payload)
  return result.data
}

export const updateReview = async (payload: any) => {
  const result = await api.put('/groups/reviews/update', payload)
  return result.data
}

export const deleteReview = async (
  brandId: number,
  groupId: number,
  reviewId: number,
) => {
  const result = await api.delete(
    `/groups/reviews/${brandId}/${groupId}/${reviewId}`,
  )
  return result.data
}

export const getServiceAreasByGroup = async (
  brandId: number,
  groupId: number,
) => {
  const result = await api.get(`/groups/service-areas/${brandId}/${groupId}`)
  return result.data
}

export const createServiceArea = async (payload: {
  brandId: number
  groupId: number
  serviceAreaIds: number[]
}) => {
  const result = await api.post(
    `/groups/service-areas/${payload.brandId}/${payload.groupId}`,
    payload,
  )
  return result.data
}

export const deleteServiceArea = async (
  brandId: number,
  groupId: number,
  serviceAreaId: number,
  createdByMemberId?: number,
) => {
  const result = await api.delete(
    `/groups/service-areas/${brandId}/${groupId}/${serviceAreaId}`,
    {
      params: {
        createdByMemberId,
      },
    },
  )
  return result.data
}
